import React, { useState, FormEvent, useEffect } from 'react';
import { useSelector } from 'store/selector';
import Appbar from 'components/appbar/Appbar';
import InsideLoading from 'components/loading/InsideLoading';
import Loading from 'components/loading/Loading';
import PageHeader from 'components/page-header/PageHeader';
import AccountForm from './AccountForm';
import AccountActions from './AccountActions';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { setUser } from 'store/modules/user/actions';
import { AppDispatch } from 'store';
import { useAuth } from 'hooks/auth/AuthElement';
import { api } from 'services/api';
import { toast } from 'react-toastify';
import { User } from 'types/user';
import { useAccountValidation } from './validation/useAccountValidation';
import { format } from 'date-fns';
import { normalizeDate } from 'helpers/parsePtBRDate';
import { UserProfile } from 'types/userProfile';

export type AccountValidation = {
  username?: string;
  email?: string;
  name?: string;
  rg?: string;
  document_number?: string;
};

const Account: React.FC = () => {
  const auth = useAuth();
  const user = useSelector(state => state.user);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [validation, setValidation, validate] = useAccountValidation();
  const [localUser, setLocalUser] = useState<User>({
    id: 0,
    name: '',
    email: '',
    document_number: '',
    rg: '',
    phone: '',
    address: '',
    active: 1,
    birth_date: null,
    created_at: '',
    email_verified_at: '',
    role: '',
    updated_at: '',
    username: '',
    user_profile: {} as UserProfile,
    user_profile_id: 0,
  });

  useEffect(() => {
    if (user) {
      setLocalUser({
        ...user,
        birth_date: user.birth_date ? normalizeDate(user.birth_date.toString()) : null,
      });
    }
  }, [user]);

  function handleChange(index: string, value: unknown) {
    setLocalUser({
      ...localUser,
      [index]: value,
    });
  }

  function handleResetEmail() {
    if (!localUser) return;
    setSaving(true);

    api
      .post('/forgot-password', { email: localUser.email })
      .then(() => {
        toast.success(`Uma mensagem de e-mail foi enviada para ${localUser.email}`);
      })
      .catch(err => {
        if (err.response) {
          toast.error(err.response.data.error);
        }
        if (!err.response) {
          toast.error('Serviço indisponível');
        }
      })
      .finally(() => {
        setSaving(false);
      });
  }

  function handleSubmit() {
    if (!localUser) return;
    setSaving(true);

    api
      .put(`/users/${localUser.id}`, {
        ...localUser,
        document_number: localUser.document_number?.replace(/\D/g, ''),
        birth_date: localUser.birth_date ? format(localUser.birth_date, 'yyyy-MM-dd HH:mm:ss') : '',
        phone: localUser.phone?.replace(/\D/g, ''),
      })
      .then(response => {
        const userBirthDate = response.data.birth_date ? response.data.birth_date.split(' ')[0] : null;
        toast.success('Salvo');
        dispatch(
          setUser({
            ...response.data,
            birth_date: userBirthDate,
          }),
        );
        setSaving(false);
      })
      .catch(err => {
        setSaving(false);
        if (err.response) {
          toast.error(err.response.data.error);
        }

        const errors = err.response.data.errors;

        const formattedErrors: AccountValidation = Object.keys(errors).reduce((acc, key) => {
          acc[key as keyof AccountValidation] = errors[key][0]; // pega só a primeira msg
          return acc;
        }, {} as AccountValidation);

        setValidation(formattedErrors);

        if (!err.response) {
          toast.error('Não foi possível salvar o usuário');
        }
      });
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();

    validate(localUser)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  return (
    <>
      {(auth.loggingOff || saving) && <Loading />}
      <Appbar title="Minha conta" ActionsComponent={<AccountActions handleSubmit={handleValidation} />} />
      {localUser.id === 0 ? (
        <InsideLoading />
      ) : (
        <div>
          <PageHeader title={`Olá, ${localUser.name}`} description="Aqui você pode alterar os dados da sua conta" />
          <AccountForm
            handleValidation={handleValidation}
            validation={validation}
            localUser={localUser}
            handleChange={handleChange}
            handleResetEmail={handleResetEmail}
          />
        </div>
      )}
    </>
  );
};

export default Account;
